// src/services/teacherActionsService.ts
import {
  addDoc,
  doc,
  getDoc,
  updateDoc,
  query,
  where,
  getDocs,
  collectionGroup,
  orderBy,
  limit,
  increment,
  Timestamp,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from 'firebase/storage';
import { tenantCollection, tenantDocument } from './tenantService';
import tenantService from './tenantService';
import { TenantCollection } from '../types/tenancy';
import studentPaymentsService from './studentPaymentsService';
import subscriptionService from './subscriptionService';
import zoomService from './zoomService';
import { auth, db, storage } from './firebase';

const MAX_ASSIGNMENT_FILES = 5;
const MAX_ASSIGNMENT_FILE_SIZE = 10 * 1024 * 1024;
const ALLOWED_ASSIGNMENT_FILE_TYPES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
  'image/webp',
  'text/plain',
]);

const sanitizeFileName = (name: string) =>
  name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(-120) || 'attachment';

const validateAssignmentFiles = (files: File[] = []) => {
  if (files.length > MAX_ASSIGNMENT_FILES) {
    throw new Error(`يمكن إرفاق ${MAX_ASSIGNMENT_FILES} ملفات كحد أقصى.`);
  }
  files.forEach(file => {
    if (file.size > MAX_ASSIGNMENT_FILE_SIZE) {
      throw new Error(`حجم الملف ${file.name} يتجاوز 10 ميجابايت.`);
    }
    if (!ALLOWED_ASSIGNMENT_FILE_TYPES.has(file.type)) {
      throw new Error(`نوع الملف ${file.name} غير مدعوم.`);
    }
  });
};

export interface SessionData {
  title: string;
  classId?: string;
  className?: string;
  classNameSnapshot?: string;
  classSubjectSnapshot?: string;
  lifecycleStatus?: 'active' | 'archived';
  subject: string;
  description: string;
  date: string;
  time: string;
  duration: number;
  type: 'online' | 'offline';
  students?: string[];
  meetingLink?: string;
  meetingType?: 'zoom' | 'google-meet' | 'other';
  meetingProvider?: 'zoom' | 'manual';
  meetingProviderMeetingId?: string | number;
  zoomMeetingId?: string | number;
  zoomMeetingDocumentId?: string;
  zoomMeetingPassword?: string;
  location?: string;
  teacherId: string;
  isActive: boolean;
  createdAt: any;
  updatedAt: any;
}

export interface AssignmentData {
  title: string;
  classId?: string;
  className?: string;
  classNameSnapshot?: string;
  classSubjectSnapshot?: string;
  lifecycleStatus?: 'active' | 'archived';
  subject: string;
  description: string;
  type: 'homework' | 'quiz' | 'exam' | 'project';
  dueDate: string;
  dueTime: string;
  totalPoints: number;
  difficulty: 'easy' | 'medium' | 'hard';
  instructions: string;
  students: string[];
  attachments?: File[];
  allowLateSubmissions: boolean;
  lateSubmissionPenalty: number;
  teacherId: string;
  isActive: boolean;
  createdAt: any;
  updatedAt: any;
}

export interface PaymentData {
  studentId: string;
  amount: number;
  method: 'cash' | 'card' | 'bank_transfer' | 'check';
  type: 'tuition' | 'registration' | 'materials' | 'exam' | 'other';
  description: string;
  referenceNumber?: string;
  dueDate?: string;
  notes: string;
  receipt: boolean;
  teacherId: string;
  status: 'pending' | 'paid' | 'overdue';
  isActive: boolean;
  createdAt: any;
  updatedAt: any;
}

class TeacherActionsService {
  private tenantCollection(teacherId: string, collectionName: TenantCollection) {
    return tenantCollection(teacherId, collectionName);
  }

  private tenantDocument(
    teacherId: string,
    collectionName: TenantCollection,
    documentId: string
  ) {
    return tenantDocument(teacherId, collectionName, documentId);
  }

  // Sessions Management
  async createSession(
    sessionData: Omit<
      SessionData,
      'teacherId' | 'isActive' | 'createdAt' | 'updatedAt'
    >,
    teacherId: string
  ): Promise<{ success: boolean; id?: string; message: string }> {
    try {
      if (!sessionData.classId) {
        throw new Error('يجب اختيار فصل للحصة');
      }
      if (!sessionData.title?.trim()) {
        throw new Error('عنوان الحصة مطلوب');
      }
      if (!Number.isFinite(sessionData.duration) || sessionData.duration <= 0) {
        throw new Error('مدة الحصة غير صحيحة');
      }

      const selectedClassRef = this.tenantDocument(
        teacherId,
        'classes',
        sessionData.classId
      );
      const selectedClassSnapshot = await getDoc(selectedClassRef);
      if (!selectedClassSnapshot.exists()) {
        throw new Error('الفصل لا يتبع هذا المعلم');
      }
      const selectedClass = selectedClassSnapshot.data();
      if (selectedClass.teacherId !== teacherId) {
        throw new Error('الفصل لا يتبع هذا المعلم');
      }
      if (
        selectedClass.isActive === false ||
        selectedClass.status === 'archived'
      ) {
        throw new Error('لا يمكن إنشاء حصة داخل فصل مؤرشف');
      }

      const limitCheck = await subscriptionService.canCreateSession(teacherId);
      if (!limitCheck.allowed) {
        throw new Error(limitCheck.messageAr);
      }

      // Convert date and time to proper timestamps
      const sessionDateTime = new Date(
        `${sessionData.date}T${sessionData.time}`
      );
      if (Number.isNaN(sessionDateTime.getTime())) {
        throw new Error('تاريخ أو وقت الحصة غير صحيح');
      }
      const endDateTime = new Date(
        sessionDateTime.getTime() + sessionData.duration * 60000
      );
      const studentIds =
        sessionData.students && sessionData.students.length > 0
          ? sessionData.students
          : selectedClass.enrolledStudents || [];

      // Also create session document
      const sessionDocData: any = {
        title: sessionData.title.trim(),
        description: sessionData.description?.trim() || '',
        subject: sessionData.subject,
        teacherId,
        tenantId: teacherId,
        classId: sessionData.classId,
        className: sessionData.className || selectedClass.name || '',
        classNameSnapshot: selectedClass.name || null,
        classSubjectSnapshot:
          selectedClass.subject || sessionData.subject || null,
        lifecycleStatus: 'active',
        studentIds,
        date: Timestamp.fromDate(sessionDateTime),
        startTime: Timestamp.fromDate(sessionDateTime),
        endTime: Timestamp.fromDate(endDateTime),
        duration: sessionData.duration,
        type: sessionData.type,
        status: 'scheduled',
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      // Add optional fields only if they exist
      if (sessionData.meetingLink) {
        sessionDocData.meetingLink = sessionData.meetingLink;
      }
      if (sessionData.meetingType) {
        sessionDocData.meetingType = sessionData.meetingType;
      }
      if (sessionData.meetingProvider) {
        sessionDocData.meetingProvider = sessionData.meetingProvider;
      }
      if (sessionData.meetingProviderMeetingId) {
        sessionDocData.meetingProviderMeetingId =
          sessionData.meetingProviderMeetingId;
      }
      if (sessionData.zoomMeetingId) {
        sessionDocData.zoomMeetingId = sessionData.zoomMeetingId;
      }
      if (sessionData.zoomMeetingDocumentId) {
        sessionDocData.zoomMeetingDocumentId = sessionData.zoomMeetingDocumentId;
      }
      if (sessionData.zoomMeetingPassword) {
        sessionDocData.zoomMeetingPassword = sessionData.zoomMeetingPassword;
      }
      if (sessionData.location) {
        sessionDocData.location = sessionData.location;
      }

      const docRef = await addDoc(
        this.tenantCollection(teacherId, 'sessions'),
        sessionDocData
      );

      await updateDoc(selectedClassRef, {
        totalSessions: increment(1),
        updatedAt: serverTimestamp(),
      });
      await subscriptionService.incrementSessionCount(teacherId);

      return {
        success: true,
        id: docRef.id,
        message: 'تم إنشاء الجلسة بنجاح',
      };
    } catch (error: any) {
      console.error('Error creating session:', error);
      return {
        success: false,
        message: 'حدث خطأ في إنشاء الجلسة: ' + error.message,
      };
    }
  }

  // Assignments Management
  async createAssignment(
    assignmentData: Omit<
      AssignmentData,
      'teacherId' | 'isActive' | 'createdAt' | 'updatedAt'
    >,
    teacherId: string
  ): Promise<{ success: boolean; id?: string; message: string }> {
    try {
      if (!assignmentData.classId) {
        throw new Error('يجب اختيار فصل للواجب');
      }
      if (!assignmentData.title?.trim()) {
        throw new Error('عنوان الواجب مطلوب');
      }
      if (
        !Number.isFinite(assignmentData.totalPoints) ||
        assignmentData.totalPoints <= 0
      ) {
        throw new Error('الدرجة العظمى غير صحيحة');
      }

      const selectedClassRef = this.tenantDocument(
        teacherId,
        'classes',
        assignmentData.classId
      );
      const selectedClassSnapshot = await getDoc(selectedClassRef);
      if (!selectedClassSnapshot.exists()) {
        throw new Error('الفصل لا يتبع هذا المعلم');
      }
      const selectedClass = selectedClassSnapshot.data();
      if (selectedClass.teacherId !== teacherId) {
        throw new Error('الفصل لا يتبع هذا المعلم');
      }
      if (
        selectedClass.isActive === false ||
        selectedClass.status === 'archived'
      ) {
        throw new Error('لا يمكن إنشاء واجب داخل فصل مؤرشف');
      }

      // Convert due date and time to timestamp
      const dueDateTime = new Date(
        `${assignmentData.dueDate}T${assignmentData.dueTime}`
      );
      if (Number.isNaN(dueDateTime.getTime())) {
        throw new Error('تاريخ أو وقت تسليم الواجب غير صحيح');
      }

      const enrolledStudents = Array.isArray(selectedClass.enrolledStudents)
        ? selectedClass.enrolledStudents
        : [];
      const selectedStudents =
        assignmentData.students && assignmentData.students.length > 0
          ? assignmentData.students
          : enrolledStudents;
      const studentIds = Array.from(new Set(selectedStudents)).filter(
        studentId => enrolledStudents.includes(studentId)
      );
      if (studentIds.length === 0) {
        throw new Error('لا يوجد طلاب مستهدفون داخل الفصل المحدد');
      }

      validateAssignmentFiles(assignmentData.attachments || []);
      const assignmentRef = doc(
        this.tenantCollection(teacherId, 'assignments')
      );
      const assignmentId = assignmentRef.id;
      const uploadedAttachments: Array<{
        id: string;
        name: string;
        url: string;
        storagePath: string;
        size: number;
        type: string;
        uploadedAt: Timestamp;
        uploadedBy: string;
      }> = [];

      try {
        for (const [index, file] of (
          assignmentData.attachments || []
        ).entries()) {
          const attachmentId = `${Date.now()}-${index}`;
          const storagePath = `teachers/${teacherId}/assignments/${assignmentId}/${attachmentId}-${sanitizeFileName(file.name)}`;
          const storageRef = ref(storage, storagePath);
          await uploadBytes(storageRef, file, {
            contentType: file.type,
            customMetadata: {
              teacherId,
              assignmentId,
              attachmentId,
              uploadedBy: auth.currentUser?.uid || teacherId,
            },
          });
          uploadedAttachments.push({
            id: attachmentId,
            name: file.name,
            url: await getDownloadURL(storageRef),
            storagePath,
            size: file.size,
            type: file.type,
            uploadedAt: Timestamp.now(),
            uploadedBy: auth.currentUser?.uid || teacherId,
          });
        }
      } catch (uploadError) {
        await Promise.allSettled(
          uploadedAttachments.map(attachment =>
            deleteObject(ref(storage, attachment.storagePath))
          )
        );
        throw uploadError;
      }

      // Create assignment document
      const assignmentDocData = {
        title: assignmentData.title.trim(),
        classId: assignmentData.classId,
        className: assignmentData.className || selectedClass.name || '',
        classNameSnapshot: selectedClass.name || null,
        classSubjectSnapshot:
          selectedClass.subject || assignmentData.subject || null,
        lifecycleStatus: 'active',
        description: assignmentData.description?.trim() || '',
        subject: assignmentData.subject || selectedClass.subject || '',
        type: assignmentData.type,
        instructions:
          assignmentData.instructions?.trim() ||
          assignmentData.description?.trim() ||
          '',
        teacherId,
        tenantId: teacherId,
        studentIds,
        dueDate: Timestamp.fromDate(dueDateTime),
        totalPoints: assignmentData.totalPoints,
        difficulty: assignmentData.difficulty,
        attachments: uploadedAttachments,
        allowLateSubmissions: assignmentData.allowLateSubmissions,
        lateSubmissionPenalty: assignmentData.lateSubmissionPenalty,
        status: 'active',
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      try {
        await setDoc(assignmentRef, assignmentDocData);
      } catch (writeError) {
        await Promise.allSettled(
          uploadedAttachments.map(attachment =>
            deleteObject(ref(storage, attachment.storagePath))
          )
        );
        throw writeError;
      }

      return {
        success: true,
        id: assignmentId,
        message: 'تم إنشاء المهمة بنجاح',
      };
    } catch (error: any) {
      console.error('Error creating assignment:', error);
      return {
        success: false,
        message: 'حدث خطأ في إنشاء المهمة: ' + error.message,
      };
    }
  }

  // Payments Management
  async createPayment(
    paymentData: Omit<
      PaymentData,
      'teacherId' | 'status' | 'isActive' | 'createdAt' | 'updatedAt'
    >,
    teacherId: string
  ): Promise<{ success: boolean; id?: string; message: string }> {
    try {
      const paymentId = await studentPaymentsService.createStudentInvoice(
        teacherId,
        {
          studentId: paymentData.studentId,
          studentName: (paymentData as any).studentName,
          amount: paymentData.amount,
          paymentMethod: paymentData.method,
          type: paymentData.type,
          description: paymentData.description,
          referenceNumber: paymentData.referenceNumber,
          dueDate: paymentData.dueDate,
          notes: paymentData.notes,
          receipt: paymentData.receipt,
        }
      );

      return {
        success: true,
        id: paymentId,
        message: 'تم إنشاء المدفوعة بنجاح',
      };
    } catch (error: any) {
      console.error('Error creating payment:', error);
      return {
        success: false,
        message: 'حدث خطأ في إنشاء المدفوعة: ' + error.message,
      };
    }
  }

  // Get teacher's sessions
  async getTeacherSessions(
    teacherId: string,
    limitCount: number = 20
  ): Promise<any[]> {
    try {
      const tenantQuery = query(
        this.tenantCollection(teacherId, 'sessions'),
        where('teacherId', '==', teacherId),
        where('isActive', '==', true),
        limit(limitCount)
      );

      const tenantSnapshot = await getDocs(tenantQuery);
      const mapSession = (docSnapshot: any) => {
        const data = docSnapshot.data();
        return {
          id: docSnapshot.id,
          ...data,
          date: data.date?.toDate(),
          startTime: data.startTime?.toDate(),
          endTime: data.endTime?.toDate(),
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        };
      };
      return tenantSnapshot.docs.map(mapSession).slice(0, limitCount);
    } catch (error) {
      console.error('Error getting teacher sessions:', error);
      return [];
    }
  }

  async getStudentSessions(
    studentId: string,
    limitCount: number = 50,
    teacherIds?: string[]
  ): Promise<any[]> {
    try {
      const resolvedTeacherIds =
        teacherIds && teacherIds.length > 0
          ? teacherIds
          : (await tenantService.getMemberships(studentId)).map(
              membership => membership.teacherId
            );
      const tenantSnapshots = await Promise.all(
        resolvedTeacherIds.map(teacherId =>
          getDocs(
            query(
              this.tenantCollection(teacherId, 'sessions'),
              where('studentIds', 'array-contains', studentId),
              where('isActive', '==', true),
              limit(limitCount)
            )
          )
        )
      );

      const mapSession = (docSnapshot: any) => {
        const data = docSnapshot.data();
        return {
          id: docSnapshot.id,
          ...data,
          date: data.date?.toDate(),
          startTime: data.startTime?.toDate(),
          endTime: data.endTime?.toDate(),
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        };
      };

      return tenantSnapshots
        .flatMap(snapshot => snapshot.docs.map(mapSession))
        .slice(0, limitCount);
    } catch (error) {
      console.error('Error getting student sessions:', error);
      return [];
    }
  }

  // Get teacher's assignments
  async getTeacherAssignments(
    teacherId: string,
    limitCount: number = 20
  ): Promise<any[]> {
    try {
      const tenantQuery = query(
        this.tenantCollection(teacherId, 'assignments'),
        where('teacherId', '==', teacherId),
        where('isActive', '==', true),
        orderBy('dueDate', 'desc'),
        limit(limitCount)
      );

      const tenantSnapshot = await getDocs(tenantQuery);
      const mapAssignment = (docSnapshot: any) => {
        const data = docSnapshot.data();
        return {
          id: docSnapshot.id,
          ...data,
          dueDate: data.dueDate?.toDate(),
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        };
      };
      return tenantSnapshot.docs.map(mapAssignment).slice(0, limitCount);
    } catch (error) {
      console.error('Error getting teacher assignments:', error);
      return [];
    }
  }

  async getStudentAssignments(
    studentId: string,
    limitCount: number = 50,
    teacherIds?: string[]
  ): Promise<any[]> {
    try {
      const mapAssignment = (docSnapshot: any) => {
        const data = docSnapshot.data();
        return {
          id: docSnapshot.id,
          ...data,
          dueDate: data.dueDate?.toDate(),
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        };
      };

      if (!teacherIds || teacherIds.length === 0) {
        const snapshot = await getDocs(
          query(
            collectionGroup(db, 'assignments'),
            where('studentIds', 'array-contains', studentId),
            limit(limitCount)
          )
        );
        return snapshot.docs
          .map(mapAssignment)
          .filter(assignment => assignment.isActive !== false)
          .sort(
            (first, second) =>
              (second.dueDate?.getTime?.() || 0) -
              (first.dueDate?.getTime?.() || 0)
          )
          .slice(0, limitCount);
      }

      const tenantSnapshots = await Promise.all(
        teacherIds.map(teacherId =>
          getDocs(
            query(
              this.tenantCollection(teacherId, 'assignments'),
              where('studentIds', 'array-contains', studentId),
              where('isActive', '==', true),
              orderBy('dueDate', 'desc'),
              limit(limitCount)
            )
          )
        )
      );

      return tenantSnapshots
        .flatMap(snapshot => snapshot.docs.map(mapAssignment))
        .slice(0, limitCount);
    } catch (error) {
      console.error('Error getting student assignments:', error);
      return [];
    }
  }

  // Get teacher's payments
  async getTeacherPayments(
    teacherId: string,
    limitCount: number = 20
  ): Promise<any[]> {
    try {
      return (await studentPaymentsService.listTeacherPayments(teacherId))
        .filter(payment => payment.isActive !== false)
        .slice(0, limitCount);
    } catch (error) {
      console.error('Error getting teacher payments:', error);
      return [];
    }
  }

  // Update session
  async updateSession(
    teacherId: string,
    sessionId: string,
    updates: Partial<SessionData>
  ): Promise<{ success: boolean; message: string }> {
    try {
      const sessionRef = this.tenantDocument(teacherId, 'sessions', sessionId);
      const tenantSnapshot = await getDoc(sessionRef);
      if (!tenantSnapshot.exists()) {
        throw new Error('Session does not belong to teacher');
      }
      const existing = tenantSnapshot.data() as Partial<SessionData>;
      if (
        updates.classId !== undefined &&
        updates.classId !== existing.classId
      ) {
        throw new Error('لا يمكن تغيير الفصل المرتبط بالحصة من هذا المسار');
      }
      if (updates.lifecycleStatus !== undefined) {
        throw new Error('لا يمكن تغيير دورة حياة الحصة من مسار التعديل العام');
      }
      await updateDoc(sessionRef, {
        ...updates,
        teacherId,
        tenantId: teacherId,
        updatedAt: serverTimestamp(),
      });

      return {
        success: true,
        message: 'تم تحديث الجلسة بنجاح',
      };
    } catch (error: any) {
      console.error('Error updating session:', error);
      return {
        success: false,
        message: 'حدث خطأ في تحديث الجلسة: ' + error.message,
      };
    }
  }

  // Delete session (soft delete)
  async deleteSession(
    teacherId: string,
    sessionId: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      const sessionRef = this.tenantDocument(teacherId, 'sessions', sessionId);
      const tenantSnapshot = await getDoc(sessionRef);
      if (!tenantSnapshot.exists()) {
        throw new Error('Session does not belong to teacher');
      }
      const session = tenantSnapshot.data() as Partial<SessionData>;
      const provider =
        session.meetingProvider ||
        (session.meetingType === 'zoom' ? 'zoom' : undefined);
      const zoomMeetingDocumentId =
        session.meetingProviderMeetingId || session.zoomMeetingDocumentId;

      let externalDeleteWarning = '';
      try {
        if (
          provider === 'zoom' &&
          typeof zoomMeetingDocumentId === 'string'
        ) {
          await zoomService.deleteMeeting(zoomMeetingDocumentId);
        }
      } catch (error) {
        console.warn('Could not delete external meeting:', error);
        externalDeleteWarning =
          ' تم حذف الحصة، لكن تعذر حذف الاجتماع الخارجي تلقائياً.';
      }

      await updateDoc(sessionRef, {
        isActive: false,
        lifecycleStatus: 'archived',
        archivedAt: serverTimestamp(),
        ...(provider === 'zoom' && typeof zoomMeetingDocumentId === 'string'
          ? {
              zoomDetachedAt: serverTimestamp(),
              zoomDetachmentReason: externalDeleteWarning
                ? 'provider_delete_failed'
                : 'provider_deleted',
            }
          : {}),
        updatedAt: serverTimestamp(),
      });

      return {
        success: true,
        message: `تم حذف الجلسة بنجاح.${externalDeleteWarning}`,
      };
    } catch (error: any) {
      console.error('Error deleting session:', error);
      return {
        success: false,
        message: 'حدث خطأ في حذف الجلسة: ' + error.message,
      };
    }
  }

  // Get submissions for all of a teacher's assignments
  async getTeacherSubmissions(teacherId: string, limitCount: number = 100): Promise<any[]> {
    try {
      const assignmentsQuery = query(
        this.tenantCollection(teacherId, 'assignments'),
        where('teacherId', '==', teacherId),
        where('isActive', '==', true)
      );
      const assignmentSnap = await getDocs(assignmentsQuery);
      const assignmentIds = Array.from(
        new Set(assignmentSnap.docs.map(d => d.id))
      );

      if (assignmentIds.length === 0) return [];

      const allSubmissions: any[] = [];
      // Firestore `in` supports max 10 items
      for (let i = 0; i < assignmentIds.length; i += 10) {
        const chunk = assignmentIds.slice(i, i + 10);
        const snapshot = await getDocs(
          query(
            this.tenantCollection(teacherId, 'submissions'),
            where('assignmentId', 'in', chunk),
            limit(limitCount)
          )
        );

        snapshot.forEach(docSnap => {
          const data = docSnap.data();
          allSubmissions.push({
            id: docSnap.id,
            ...data,
            submittedAt: data.submittedAt?.toDate() ?? new Date(),
            gradedAt: data.gradedAt?.toDate(),
          });
        });
      }
      return allSubmissions.slice(0, limitCount);
    } catch (error) {
      console.error('Error getting teacher submissions:', error);
      return [];
    }
  }

  // Grade a student submission
  async gradeSubmission(
    teacherId: string,
    submissionId: string,
    grade: number,
    feedback: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      const submissionRef = this.tenantDocument(
        teacherId,
        'submissions',
        submissionId
      );
      const tenantSnapshot = await getDoc(submissionRef);
      if (!tenantSnapshot.exists()) {
        throw new Error('Submission does not belong to teacher');
      }
      await updateDoc(submissionRef, {
        grade,
        feedback,
        status: 'graded',
        gradedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return { success: true, message: 'تم حفظ التقييم بنجاح' };
    } catch (error: any) {
      console.error('Error grading submission:', error);
      return { success: false, message: 'خطأ في حفظ التقييم: ' + error.message };
    }
  }

  // Update payment status
  async updatePaymentStatus(
    teacherId: string,
    paymentId: string,
    status: 'paid' | 'pending' | 'overdue'
  ): Promise<{ success: boolean; message: string }> {
    try {
      await studentPaymentsService.updateStudentPaymentStatus(
        teacherId,
        paymentId,
        status
      );
      return { success: true, message: 'تم تحديث حالة الدفعة بنجاح' };
    } catch (error: any) {
      console.error('Error updating payment status:', error);
      return { success: false, message: 'خطأ في تحديث الحالة: ' + error.message };
    }
  }
}

const teacherActionsService = new TeacherActionsService();
export default teacherActionsService;
